import type { IEventBus } from '../events/IEventBus';
import type { ICommand } from '../commands/ICommand';
import type { CommandManager } from '../commands/CommandManager';
import { useDiagramStore } from '../../stores/diagramStore';
import type { Edge, Node } from '../models/scene';
import { AutoPathEdgesCommand, type AutoPathEdgeUpdate } from '../commands/edges/AutoPathEdgesCommand';

type AnchorSide = 'top' | 'right' | 'bottom' | 'left';

type Rect = {
  x: number;
  y: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
};

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

const getRect = (n: Node): Rect => {
  const anyN: any = n as any;
  const x = isFiniteNumber(anyN.rectX) ? anyN.rectX : (n.x ?? 0);
  const y = isFiniteNumber(anyN.rectY) ? anyN.rectY : (n.y ?? 0);
  const w =
    (isFiniteNumber(anyN.rectW) ? anyN.rectW : undefined) ??
    (isFiniteNumber((n as any).w) ? (n as any).w : undefined) ??
    (isFiniteNumber(n.width) ? n.width : undefined) ??
    120;
  const h =
    (isFiniteNumber(anyN.rectH) ? anyN.rectH : undefined) ??
    (isFiniteNumber((n as any).h) ? (n as any).h : undefined) ??
    (isFiniteNumber(n.height) ? n.height : undefined) ??
    80;
  return { x, y, w, h, cx: x + w / 2, cy: y + h / 2 };
};

const sideCenter = (r: Rect, side: AnchorSide): { x: number; y: number } => {
  switch (side) {
    case 'top': return { x: r.cx, y: r.y };
    case 'right': return { x: r.x + r.w, y: r.cy };
    case 'bottom': return { x: r.cx, y: r.y + r.h };
    case 'left': return { x: r.x, y: r.cy };
  }
};

const chooseIdealAnchorPair = (fromNode: Node, toNode: Node): { from: AnchorSide; to: AnchorSide } => {
  const a = getRect(fromNode);
  const b = getRect(toNode);
  const dx = b.cx - a.cx;
  const dy = b.cy - a.cy;

  const horizontal = dx >= 0
    ? ({ from: 'right', to: 'left' } as const)
    : ({ from: 'left', to: 'right' } as const);
  const vertical = dy >= 0
    ? ({ from: 'bottom', to: 'top' } as const)
    : ({ from: 'top', to: 'bottom' } as const);

  const ha = sideCenter(a, horizontal.from);
  const hb = sideCenter(b, horizontal.to);
  const va = sideCenter(a, vertical.from);
  const vb = sideCenter(b, vertical.to);
  const dh2 = (ha.x - hb.x) ** 2 + (ha.y - hb.y) ** 2;
  const dv2 = (va.x - vb.x) ** 2 + (va.y - vb.y) ** 2;

  return dh2 <= dv2 ? horizontal : vertical;
};

const isCandidateEdge = (edge: Edge): boolean => {
  const t = (edge as any).type as string | undefined;
  if (t !== 'line' && t !== 'sequence_flow') return false;
  const fromId = (edge as any).from as string | undefined;
  const toId = (edge as any).to as string | undefined;
  if (!fromId || !toId) return false;
  if (fromId.startsWith('__free') || toId.startsWith('__free')) return false;
  const attrs: any = (edge as any).attrs || {};
  if (attrs.fromPoint || attrs.toPoint) return false;
  // Don't auto-path manual/waypoint-routed connectors
  if (attrs.routingType === 'manual') return false;
  if (Array.isArray(attrs.waypoints) && attrs.waypoints.length > 0) return false;
  return true;
};

const computeEdgeAnchorUpdate = (edge: Edge, nodesById: Map<string, Node>): AutoPathEdgeUpdate | null => {
  if (!isCandidateEdge(edge)) return null;
  const fromId = (edge as any).from as string;
  const toId = (edge as any).to as string;
  const fromNode = nodesById.get(fromId);
  const toNode = nodesById.get(toId);
  if (!fromNode || !toNode) return null;

  const pair = chooseIdealAnchorPair(fromNode, toNode);

  const fromLocked = Boolean((edge as any).fromAnchorLocked);
  const toLocked = Boolean((edge as any).toAnchorLocked);
  const prevFrom = (edge as any).fromAnchor as AnchorSide | undefined;
  const prevTo = (edge as any).toAnchor as AnchorSide | undefined;

  const updates: Record<string, unknown> = {};

  if (!fromLocked) {
    if (prevFrom !== pair.from) {
      updates.fromAnchor = pair.from;
      updates.fromOffset = 0.5;
    } else if (!isFiniteNumber((edge as any).fromOffset)) {
      updates.fromOffset = 0.5;
    }
  }

  if (!toLocked) {
    if (prevTo !== pair.to) {
      updates.toAnchor = pair.to;
      updates.toOffset = 0.5;
    } else if (!isFiniteNumber((edge as any).toOffset)) {
      updates.toOffset = 0.5;
    }
  }

  if (Object.keys(updates).length === 0) return null;
  return { edgeId: edge.id, updates: updates as Partial<Edge> };
};

type Dispose = () => void;

export class AutoConnectorPathingEngine {
  private eventBus: IEventBus;
  private commandManager: CommandManager;
  private timer: any;
  private unsubscribes: Dispose[] = [];
  private isRunning = false;
  private pending = false;

  constructor(eventBus: IEventBus, commandManager: CommandManager) {
    this.eventBus = eventBus;
    this.commandManager = commandManager;
  }

  start(): void {
    const onUndoRedo = (payload: any) => {
      const cmd = payload?.command as ICommand | undefined;
      if (cmd?.name === 'Auto Path Edges') return;
      this.schedule();
    };

    // Important: do NOT auto-path on every execute. During drag/drop we may already
    // have the correct preview anchors; re-pathing on execute can cause a visible
    // anchor flip right after drop. We only re-path after undo/redo to keep anchors
    // deterministic across history operations.
    this.unsubscribes.push(
      this.eventBus.on('command:undone', onUndoRedo),
      this.eventBus.on('command:redone', onUndoRedo),
    );
  }

  dispose(): void {
    this.unsubscribes.forEach((u) => u());
    this.unsubscribes = [];
    if (this.timer) clearTimeout(this.timer);
  }

  private schedule(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.run(), 50);
  }

  private async run(): Promise<void> {
    if (this.isRunning) {
      this.pending = true;
      return;
    }
    this.isRunning = true;
    this.pending = false;

    try {
      const store = useDiagramStore.getState();
      if (!store.canWrite) return;

      const nodesById = new Map<string, Node>((store.nodes as Node[]).map((n) => [n.id, n]));
      const edgeUpdates: AutoPathEdgeUpdate[] = [];

      for (const edge of store.edges as Edge[]) {
        const update = computeEdgeAnchorUpdate(edge, nodesById);
        if (update) edgeUpdates.push(update);
      }

      if (edgeUpdates.length === 0) return;
      await this.commandManager.execute(new AutoPathEdgesCommand(edgeUpdates));
    } finally {
      this.isRunning = false;
      if (this.pending) this.schedule();
    }
  }
}
